import React, { useEffect, useRef, useState, useMemo } from 'react';
import KPITile from '../../../../../common/KPITile/KPITile';
import KPIDetailTile from '../../../../../common/KPIDetailTile/KPIDetailTile';
import VarianceValue from '../../../../../common/VarianceValue/VarianceValue';
import styles from './PersonalPvTile.module.scss';
import { useTranslation } from 'react-i18next';
import ViewModeSwitcher, {
  ViewMode,
} from '../../../../../common/ViewModeSwitcher/ViewModeSwitcher';
import GraphCard from '../../../../../common/GraphCard/GraphCard';
import GenericTable from '../../../../../common/GenericTable/GenericTable';
import InsightAI from '../../../../../common/InsightAI/InsightAI';
import isEmpty from 'lodash/isEmpty';
import { useGetAGCAIDetailsMutation } from '../../../../../../redux/services/agcAIAPI';
import { useGetBusinessPerformanceDetailsQuery } from '../../../../../../redux/services/businessPerformaceAPI';
import { useProfileParams, isAISummariesAccessible } from '../../../../../../utils/common';
import StackedBarChart from '../../../../../common/StackedBarChart/StackedBarChart';
import Skeleton from '../../../../../common/Skeleton/Skeleton';
import { isValidLineChartData } from '../../../../../../utils/common';
import { sanitizeStackedBarData } from '../../../../../../utils/stackedBarChartDataSanitizer';
import { valueText } from '../../../../../../utils/common';
import { useAppSelector } from '../../../../../../redux/hooks';
import { NumberFormatter } from '../../../../../../utils/NumberFormatter';
interface SummaryProp {
  summary?: any;
  loading?: boolean;
}

const PersonalPvTile: React.FC<SummaryProp> = ({ summary, loading }) => {
  const { t } = useTranslation();
  const userDetails = useAppSelector((state: any) => state.userDetails.user);
  const [userAffiliateCode] = useState(userDetails?.userAffiliateCode || 0);

  const valueNum = typeof summary?.kpiValue === 'number' ? summary?.kpiValue : undefined;
  return (
    <KPITile title={t('personalPv.title')} dataTestId="personal-pv-tile" loading={loading}>
      <div className={styles.tile_content}>
        <div className={styles.value_wrapper}>
          <div className={styles.value_content}>
            {NumberFormatter(userAffiliateCode, valueNum, true)}
          </div>
          <VarianceValue
            value={summary?.kpiVarianceValue ?? 0}
            dataType={summary?.kpiVarianceDataType}
            comparisonCriteria={summary?.kpiVarianceComparisonCriteria}
            truncateDecimals
          />
        </div>
      </div>
    </KPITile>
  );
};

export default PersonalPvTile;

// ---- Sub-components for Views ---- //

interface PersonalPvMonthlyViewProps {
  viewMode: ViewMode;
  lineChartData: any;
  tableData: any;
  currency: string;
  chartSummaryData: any;
  kpiCode: string;
}

const PersonalPvMonthlyView: React.FC<PersonalPvMonthlyViewProps> = ({
  viewMode,
  lineChartData,
  tableData,
  currency,
  chartSummaryData,
  kpiCode,
}) => {
  const { t } = useTranslation();

  if (viewMode === 'left') {
    return isEmpty(lineChartData) ? (
      <div className={styles.no_data}>{t('noDataAvailable')}</div>
    ) : (
      <GraphCard
        lineChartData={lineChartData}
        currency={currency}
        chartSummaryData={chartSummaryData}
        truncateDecimals
      />
    );
  }

  return isEmpty(tableData) ? (
    <div className={styles.no_data}>{t('noDataAvailable')}</div>
  ) : (
    <GenericTable
      tableData={tableData}
      kpiCode={kpiCode}
      highlightRows={[0]}
      isOnlyNegativeHighlight={true}
      truncateDecimals
    />
  );
};

interface PersonalPvYearlyViewProps {
  viewMode: ViewMode;
  stackedBarChartData: any;
  tableData: any;
  kpiCode: string;
  yAxisValues?: number[];
}

const PersonalPvYearlyView: React.FC<PersonalPvYearlyViewProps> = ({
  viewMode,
  stackedBarChartData,
  tableData,
  kpiCode,
  yAxisValues,
}) => {
  const { t } = useTranslation();
  const userDetails = useAppSelector((state: any) => state.userDetails.user);
  const [userAffiliateCode] = useState(userDetails?.userAffiliateCode || 0);

  if (viewMode === 'left') {
    return isEmpty(stackedBarChartData) ? (
      <div className={styles.no_data}>{t('noDataAvailable')}</div>
    ) : (
      <StackedBarChart
        data={stackedBarChartData}
        yAxisValues={yAxisValues}
        affiliateCode={userAffiliateCode}
        truncateDecimals
      />
    );
  }

  return isEmpty(tableData) ? (
    <div className={styles.no_data}>{t('noDataAvailable')}</div>
  ) : (
    <GenericTable tableData={tableData} kpiCode={kpiCode} highlightColumns={[1]} truncateDecimals showSTLY />
  );
};

export const PersonalPvDetail: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('left'); // chart/table switcher
  const [periodCode, setPeriodCode] = useState<string>('monthly');
  const kpiCode = 'personalPV';
  const { affiliateCode, aboNumber } = useProfileParams();

  const { data: detailsResponse, isLoading } = useGetBusinessPerformanceDetailsQuery(
    { affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } as any },
    { skip: !onClose }
  );

  const [getAGCAIDetails, { data: aiDetailsResp, isLoading: isLoadingAiSummary }] =
    useGetAGCAIDetailsMutation();
  const hasRequestedRef = useRef(false);
  useEffect(() => {
    const canFetch = Number(affiliateCode) !== 0 && aboNumber !== 0 && Boolean(onClose);
    if (!canFetch || hasRequestedRef.current) return;
    hasRequestedRef.current = true;
    getAGCAIDetails({ affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } as any });
  }, [affiliateCode, aboNumber, onClose, kpiCode, getAGCAIDetails]);

  const aiRaw = aiDetailsResp?.body;
  const aiResolved = typeof aiRaw === 'string' ? aiRaw : aiRaw?.[kpiCode] || aiRaw?.summary || '';
  const baseAI = aiResolved ? String(aiResolved).slice(0, 300).concat('...') : '';
  const aiContent = baseAI
    ? `${periodCode === 'monthly' ? '[Monthly] ' : '[Yearly] '}${baseAI}`
    : '';

  const detailData = detailsResponse?.body?.kpis?.[0] || {};
  const currency = detailData?.kpiCurrency || 'USD';

  // Extract monthly view data
  const monthlyCharts = detailData?.detail?.monthlyView?.chart || [];
  const monthlyLineChart = monthlyCharts.find((chart: any) => chart?.chartType === 'lineChart');
  const monthlyTableChart = monthlyCharts.find((chart: any) => chart?.chartType === 'table');
  const lineChartData = monthlyLineChart?.chartDetail?.lineChartDetail;
  const monthlyTableData = monthlyTableChart?.chartDetail?.tableDetail;

  // Extract annually view data (handle typo "anuallyView" in API response)
  const annuallyCharts =
    detailData?.detail?.annuallyView?.chart || detailData?.detail?.anuallyView?.chart || [];
  const annuallyBarChart = annuallyCharts.find(
    (chart: any) => chart?.chartType === 'barChart' || chart?.chartType === 'stackedBarChart'
  );
  const annuallyTableChart = annuallyCharts.find((chart: any) => chart?.chartType === 'table');
  const barChartDetail =
    annuallyBarChart?.chartDetail?.barChartDetail ||
    annuallyBarChart?.chartDetail?.stackedBarChartDetail;
  const annuallyTableData = annuallyTableChart?.chartDetail?.tableDetail;

  const yAxisValues = useMemo(() => {
    if (!barChartDetail?.yAxis || !Array.isArray(barChartDetail.yAxis)) return undefined;
    return barChartDetail.yAxis.map((item: any) => Number(item.dataValue));
  }, [barChartDetail]);

  // Transform barChartDetail to StackedBarChart format if needed
  const stackedBarChartData = useMemo(
    () => sanitizeStackedBarData(barChartDetail, t('personalPv.title')),
    [barChartDetail, t]
  );

  const tableData = periodCode === 'monthly' ? monthlyTableData : annuallyTableData;
  const hasValidLineChart = isValidLineChartData(lineChartData);
  const hasChartData = periodCode === 'monthly' ? hasValidLineChart : !isEmpty(stackedBarChartData);

  const periodItems = useMemo(
    () => [
      { code: 'monthly', label: t('monthly_view') },
      { code: 'yearly', label: t('yearly_view') },
    ],
    [t]
  );

  if (isLoading) return <Skeleton height="400px" />;

  const headerContent = (
    <div className={styles.header_content}>
      <div className={styles.tile_header}>
        <div className={styles.title}>{t('personalPv.title')}</div>
        <div className={styles.desc}>{t('personalPv.description')}</div>
      </div>
      <ViewModeSwitcher
        mode={viewMode}
        onChangeMode={setViewMode}
        disabled={false}
        timeRanges={periodItems}
        selectedTimeRangeCode={periodCode}
        onSelectTimeRange={setPeriodCode}
      />
    </div>
  );

  const leftContent =
    periodCode === 'monthly' ? (
      <PersonalPvMonthlyView
        viewMode={viewMode}
        lineChartData={hasValidLineChart ? lineChartData : undefined}
        tableData={tableData}
        currency={currency}
        chartSummaryData={monthlyLineChart?.chartSummary}
        kpiCode={kpiCode}
      />
    ) : (
      <PersonalPvYearlyView
        viewMode={viewMode}
        stackedBarChartData={stackedBarChartData}
        tableData={tableData}
        kpiCode={kpiCode}
        yAxisValues={yAxisValues}
      />
    );

  const rightContent = isAISummariesAccessible() ? (
    isLoadingAiSummary ? (
      <Skeleton height="200px" />
    ) : (
      <InsightAI
        updateDate=""
        content={aiContent}
        title={t('ai_summary')}
        showDropdown={false}
        isDropdownEnabled={true}
        flowerIconSize={20}
        infoIconSize={12}
        titleSize={14}
      />
    )
  ) : null;

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="personal-pv-detail"
    />
  );
};
